import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { hrNotificationKeys } from "@/lib/query";
import type { HrRecipientPurpose } from "@/schemas/hr-notification";

export interface HrNotificationRecipient {
  id: string;
  email: string;
  purpose: HrRecipientPurpose;
  createdAt: string;
}

export function useHrNotificationRecipients(purpose: HrRecipientPurpose, enabled = true) {
  return useQuery({
    queryKey: hrNotificationKeys.list(purpose),
    queryFn: async (): Promise<HrNotificationRecipient[]> => {
      const res = await fetch(`/api/config/hr-notification-emails?purpose=${purpose}`);
      if (!res.ok) throw new Error("Failed to fetch notification recipients");
      const json = await res.json();
      return json.data;
    },
    enabled,
  });
}

/** Replace the whole recipient set for a purpose with the given addresses. */
export function useSetHrNotificationRecipients(purpose: HrRecipientPurpose) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (emails: string[]) => {
      const res = await fetch(`/api/config/hr-notification-emails?purpose=${purpose}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emails }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Save failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrNotificationKeys.list(purpose) });
      toast.success("Recipients saved");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to save recipients"),
  });
}
